import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreditConfigurationInfo,
  ENVIRONMENT,
  LoanDetailResponse,
  LoanLockResponse,
  LoanURLA,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class LoanDetailsService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async getLoanDetails(loanId: string): Promise<LoanDetailResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}`;

    try {
      return await firstValueFrom(this.http.get<LoanDetailResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching loan details for ${loanId}`);
      throw new Error(`Failed to fetch loan details: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getPaymentLockStatus(loanId: string): Promise<LoanLockResponse> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/paymentlockstatus`;

    try {
      return await firstValueFrom(this.http.get<LoanLockResponse>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching payment lock status for ${loanId}`);
      throw new Error(`Failed to fetch payment lock status: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getURLAs(loanId: string): Promise<LoanURLA> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/urlas`;

    try {
      return await firstValueFrom(this.http.get<LoanURLA>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching URLAs for ${loanId}`);
      throw new Error(`Failed to fetch URLAs: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async getCreditCardConfiguration(loanId: string): Promise<CreditConfigurationInfo> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${loanId}/creditcardconfigurations`;

    try {
      return await firstValueFrom(this.http.get<CreditConfigurationInfo>(url));
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error fetching credit card configuration for ${loanId}`);
      throw new Error(`Failed to fetch credit card configuration: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
}
